'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import { usePathname } from 'next/navigation';
import { X } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { apiService } from '@/lib/api';

const DISMISS_KEY = 'newsletter_prompt_dismiss_until';
const SUBMITTED_KEY = 'newsletter_prompt_submitted';
// Respect the visitor: don't re-ask for 3 days after "remind me later"
const REMIND_LATER_DELAY_MS = 3 * 24 * 60 * 60 * 1000;

const getRandomDelayMs = () => {
  // Let people actually read the site before asking for their email
  const min = 150_000;
  const max = 210_000;
  return Math.floor(Math.random() * (max - min + 1)) + min;
};

export default function NewsletterPrompt() {
  const pathname = usePathname();
  const { isAuthenticated, user } = useAuth();
  const [visible, setVisible] = useState(false);
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const isLoggedIn = useMemo(() => isAuthenticated || !!user, [isAuthenticated, user]);

  const clearPromptTimer = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  const schedulePrompt = () => {
    clearPromptTimer();

    if (typeof window === 'undefined' || isLoggedIn) return;

    const submitted = localStorage.getItem(SUBMITTED_KEY) === '1';
    if (submitted) return;

    const dismissedUntil = Number(localStorage.getItem(DISMISS_KEY) || '0');
    const now = Date.now();
    const wait = dismissedUntil > now ? Math.max(dismissedUntil - now, 1_000) : getRandomDelayMs();

    timerRef.current = setTimeout(() => {
      setVisible(true);
      apiService.trackUserCrawl({
        eventType: 'newsletter_popup_shown',
        pagePath: pathname || '/',
        section: 'newsletter_popup',
        action: 'show',
        metadata: {
          component: 'newsletter_popup',
          loggedIn: false,
        },
      }).catch(() => {});
    }, wait);
  };

  useEffect(() => {
    if (isLoggedIn) {
      setVisible(false);
      clearPromptTimer();
      return;
    }

    schedulePrompt();
    apiService.trackUserCrawl({
      eventType: 'page_view',
      pagePath: pathname || '/',
      section: 'page',
      action: 'view',
      metadata: {
        component: 'page',
      },
    }).catch(() => {});

    return () => {
      clearPromptTimer();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, isLoggedIn]);

  const remindMeLater = () => {
    setVisible(false);
    const remindAfter = Date.now() + REMIND_LATER_DELAY_MS;
    localStorage.setItem(DISMISS_KEY, String(remindAfter));

    apiService.trackUserCrawl({
      eventType: 'newsletter_popup_remind_later',
      pagePath: pathname || '/',
      section: 'newsletter_popup',
      action: 'remind_me_later',
      metadata: {
        component: 'newsletter_popup',
        remindAfterSeconds: REMIND_LATER_DELAY_MS / 1000,
      },
    }).catch(() => {});

    schedulePrompt();
  };

  const submitNewsletter = async (event: React.FormEvent) => {
    event.preventDefault();
    setError('');

    const trimmedEmail = email.trim().toLowerCase();
    if (!/^\S+@\S+\.\S+$/.test(trimmedEmail)) {
      setError('Please enter a valid email address');
      return;
    }

    try {
      setLoading(true);
      await apiService.saveNewsletterLead(trimmedEmail, {
        pagePath: pathname || '/',
        status: 'non-logged-in-user',
        component: 'newsletter_popup',
      });

      await apiService.trackUserCrawl({
        eventType: 'newsletter_popup_submit',
        pagePath: pathname || '/',
        section: 'newsletter_popup',
        action: 'submit_email',
        metadata: {
          component: 'newsletter_popup',
          email: trimmedEmail,
        },
      });

      localStorage.setItem(SUBMITTED_KEY, '1');
      setVisible(false);
      setEmail('');
    } catch (err: any) {
      setError(err?.message || 'Failed to save email');
    } finally {
      setLoading(false);
    }
  };

  if (!visible || isLoggedIn) return null;

  return (
    <div className="fixed inset-0 z-[120] flex items-end sm:items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-md rounded-xl border border-gray-200 dark:border-dark-700 bg-white dark:bg-dark-800 shadow-2xl p-5">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Join our newsletter</h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">Sign up for updates and spiritual insights.</p>
          </div>
          <button
            onClick={remindMeLater}
            className="p-1 rounded hover:bg-gray-100 dark:hover:bg-dark-700"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <form className="mt-4 space-y-3" onSubmit={submitNewsletter}>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Enter your email"
            className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-dark-600 bg-white dark:bg-dark-900"
            required
          />

          {error && <p className="text-sm text-red-600">{error}</p>}

          <div className="flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={remindMeLater}
              className="px-3 py-2 rounded-lg border border-gray-300 dark:border-dark-600 text-sm"
            >
              Remind me later
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 rounded-lg bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-60 text-sm"
            >
              {loading ? 'Saving...' : 'Sign up'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
